import { useCallback, useRef } from 'react';

interface TouchPosition {
  x: number;
  y: number;
  time: number;
}

interface UseSwipeGesturesProps {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeStart?: () => void;
  onSwipeEnd?: () => void;
  threshold?: number;
  velocityThreshold?: number;
}

interface UseSwipeGesturesReturn {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
}

export function useSwipeGestures({
  onSwipeLeft,
  onSwipeRight,
  onSwipeStart,
  onSwipeEnd,
  threshold = 50,
  velocityThreshold = 0.3
}: UseSwipeGesturesProps): UseSwipeGesturesReturn {
  const touchStart = useRef<TouchPosition | null>(null);
  const touchMove = useRef<TouchPosition | null>(null);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      touchStart.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now()
      };
      touchMove.current = null;
      onSwipeStart?.();
    },
    [onSwipeStart]
  );

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStart.current) return;

    const touch = e.touches[0];
    touchMove.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStart.current) return;

      const touchEnd = touchMove.current || {
        x: e.changedTouches[0].clientX,
        y: e.changedTouches[0].clientY,
        time: Date.now()
      };

      const deltaX = touchEnd.x - touchStart.current.x;
      const deltaY = touchEnd.y - touchStart.current.y;
      const deltaTime = touchEnd.time - touchStart.current.time;

      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      const velocity = absX / deltaTime;

      // Check if it's a horizontal swipe (more horizontal than vertical movement)
      if (absX > absY && absX > threshold && velocity > velocityThreshold) {
        if (deltaX > 0) {
          onSwipeRight?.();
        } else {
          onSwipeLeft?.();
        }
      }

      touchStart.current = null;
      touchMove.current = null;
      onSwipeEnd?.();
    },
    [onSwipeLeft, onSwipeRight, onSwipeEnd, threshold, velocityThreshold]
  );

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd
  };
}
